import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { api } from '../contexts/AuthContext';
import { 
  FileSpreadsheet, 
  Sparkles,
  Send,
  Loader2,
  ArrowLeft,
  Upload,
  FileText,
  Search,
  Trash2,
  User
} from 'lucide-react';


const AIAssistantPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('clean'); // clean, dedupe
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [filesLoading, setFilesLoading] = useState(true);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await api.get('/api/projects');
      
      // Get files from all projects
      const allFiles = [];
      for (const project of response.data) {
        const projectRes = await api.get(`/api/projects/${project.project_id}`);
        if (projectRes.data.files) {
          allFiles.push(...projectRes.data.files);
        }
      }
      setFiles(allFiles);
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !prompt.trim()) {
      toast.error('Please select a file and enter a prompt');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let response;
      
      if (mode === 'dedupe') {
        // Get file columns first
        const fileRes = await api.get(`/api/files/${selectedFile}`);
        
        response = await api.post('/api/ai/deduplicate', null, {
          params: {
            file_id: selectedFile,
            prompt: prompt
          },
          data: fileRes.data.columns
        });
      } else {
        response = await api.post('/api/ai/smart-clean', {
          file_id: selectedFile,
          prompt: prompt
        });
      }
      
      setResult(response.data);
      toast.success('AI analysis complete!');
    } catch (error) {
      console.error('AI request error:', error);
      toast.error('AI request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const suggestedPrompts = {
    clean: [
      "Clean all phone numbers and format them consistently",
      "Fix email typos and extract domains",
      "Standardize all country and state names",
      "Title case all name columns",
      "Remove extra whitespace from all columns"
    ],
    dedupe: [
      "Find duplicate contacts by email",
      "Identify similar company names",
      "Find duplicates by name and phone",
      "Detect fuzzy duplicates across all columns"
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-heading font-bold text-slate-900">CRM Data Cleaner</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" className="w-10 h-10 rounded-full p-0">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name} 
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Panel - Input */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-rose-500 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <Sparkles className="w-8 h-8" />
                <h1 className="text-2xl font-heading font-bold">AI Data Assistant</h1>
              </div>
              <p className="text-indigo-100">
                Use natural language to clean data and find duplicates with AI.
              </p>
            </div>

            {/* Mode Selection */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">What would you like to do?</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'clean', icon: <FileText className="w-5 h-5" />, label: 'Smart Clean' },
                  { id: 'dedupe', icon: <Search className="w-5 h-5" />, label: 'Find Duplicates' }
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      mode === m.id 
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`mode-${m.id}`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      {m.icon}
                      <span className="font-medium text-sm">{m.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* File Selection */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft space-y-4">
              <div>
                <Label htmlFor="file" className="text-sm font-medium text-slate-700 mb-2 block">
                  Select File
                </Label>
                {filesLoading ? (
                  <div className="h-10 bg-slate-100 rounded-lg animate-pulse"></div>
                ) : files.length === 0 ? (
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-slate-600 mb-3">No files uploaded yet</p>
                    <Button onClick={() => navigate('/upload')} variant="outline" size="sm">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </Button>
                  </div>
                ) : (
                  <Select value={selectedFile} onValueChange={setSelectedFile}>
                    <SelectTrigger data-testid="file-select">
                      <SelectValue placeholder="Choose a file..." />
                    </SelectTrigger>
                    <SelectContent>
                      {files.map((file) => (
                        <SelectItem key={file.file_id} value={file.file_id}>
                          {file.original_name} ({file.row_count} rows)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div>
                <Label htmlFor="prompt" className="text-sm font-medium text-slate-700 mb-2 block">
                  What do you want to do?
                </Label>
                <Textarea
                  id="prompt"
                  placeholder="Describe what you want to do with your data..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[100px] resize-none"
                  data-testid="prompt-input"
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={loading || !selectedFile || !prompt.trim()}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12"
                data-testid="submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Run AI Analysis
                  </>
                )}
              </Button>
            </div>

            {/* Results */}
            {result && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft">
                <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
                  AI Results
                </h3>
                
                {mode === 'clean' && result.operations && (
                  <div className="space-y-4">
                    <p className="text-slate-600">{result.summary}</p>
                    {result.operations.length > 0 && (
                      <div className="space-y-2">
                        <p className="font-medium text-slate-700">Suggested Operations:</p>
                        {result.operations.map((op, i) => (
                          <div key={i} className="p-3 bg-indigo-50 rounded-lg">
                            <p className="font-medium text-indigo-800">{op.column}: {op.operation}</p>
                            <p className="text-sm text-indigo-600">{op.reason}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {result.warnings?.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="font-medium text-amber-800 mb-2">Warnings:</p>
                        <ul className="text-sm text-amber-700 space-y-1">
                          {result.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {mode === 'dedupe' && (
                  <div className="space-y-4">
                    <p className="text-slate-600">{result.recommendation}</p>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-700">{result.total_duplicates || 0}</p>
                      <p className="text-sm text-amber-600">Potential Duplicates</p>
                    </div>
                    {result.duplicate_groups?.length > 0 && (
                      <div>
                        <p className="font-medium text-slate-700 mb-2">Duplicate Groups:</p>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {result.duplicate_groups.map((group, i) => (
                            <div key={i} className="p-3 bg-slate-50 rounded-lg">
                              <p className="text-sm text-slate-600">Rows: {group.rows?.join(', ')}</p>
                              <p className="text-sm text-amber-600">{group.reason}</p>
                              <p className="text-xs text-slate-400">{Math.round(group.confidence * 100)}% confidence</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Suggestions */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft sticky top-28">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
                Suggested Prompts
              </h3>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {suggestedPrompts[mode]?.map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => setPrompt(suggestion)}
                      className="w-full text-left p-3 rounded-lg bg-slate-50 hover:bg-indigo-50 text-sm text-slate-700 hover:text-indigo-700 transition-colors"
                      data-testid={`suggestion-${i}`}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AIAssistantPage;
